// src/pages/EmotionPage.js
import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { createPortal } from "react-dom";
import Seo from "../components/seo";
import cardData from "../components/card-data";

const API_BASE = "https://tarotfate-api.tarotfate-com.workers.dev";


/* =========================
   Emotion meta
========================= */
const emotionMeta = {
  "heart broken": {
    title: "Tarot for Broken Heart",
    description:
      "When your heart aches and silence fills the space where love once lived, tarot becomes your gentle companion. These cards hold your story and guide you toward healing.",
    image: "/icons/Heart-broken.webp",
    emotionKey: "Heart broken",
    details: [
      "These cards guide you through the storm of sorrow and bring clarity when emotions feel overwhelming.",
      "They validate your pain, remind you that you deserve peace, and light a gentle path back to yourself.",
    ],
    cards: [
      "three-of-swords-tarot-card-reading-meaning",
      "the-tower-tarot-card-reading-meaning",
      "five-of-cups-tarot-card-reading-meaning",
    ],
    longForm: `
<p>Heartbreak can make the hours heavy. Mornings arrive too soon, nights feel endless, and every small thing carries the echo of what was lost. In these moments of aching silence, tarot can serve as a steady companion. The images and symbols do not demand that you move faster than you are ready. Instead, they hold your experience with gentleness, reminding you that sorrow can also teach, and that the heart is capable of healing in its own rhythm.</p>

<p>When you feel broken, begin with honesty. Name the feeling without judgment. Sadness, anger, numbness, or longing—whatever rises is part of your truth. Draw a small spread, perhaps three cards, and allow each one to speak to a question. What pain needs to be honored right now. What support remains around me. What gentle step can I take toward peace. Each card becomes a mirror, showing not only the ache but also the resilience living inside you.</p>

<p>The cards that often appear in this time carry profound lessons. The Three of Swords reflects the sharpness of loss but also insists that pain must be acknowledged rather than hidden. The Five of Cups teaches us that grief is real, yet not everything is lost. Some cups remain standing, and they can be honored too. The Star shines softly after the storm, guiding you to rebuild your faith in life one breath at a time. Strength reminds you that courage is not loud but patient, expressed in every quiet choice to keep going.</p>

<p>As you move through this journey, remember that healing is not about forgetting or rushing. It is about learning to carry your past with compassion instead of bitterness. Speak to yourself as you would to a friend you deeply love. Celebrate the days when you smile again, and forgive the days when the ache still returns. Both are part of the healing cycle. Tarot reminds you that every card belongs to a larger story, and your story is still unfolding.</p>

<p>To close a reading, choose one act of kindness for yourself. Step outside and feel the air on your skin. Drink a glass of water slowly. Write a message to someone who cares for you. Each act is a signal to your spirit that you are still here, that you are still worthy, and that peace is not gone—it is only waiting to be welcomed again.</p>
`,
  },

  anxious: {
    title: "Tarot for Anxiety",
    description:
      "Anxiety feels like a restless tide of thoughts and fears. Tarot offers calm structure, gentle reflection, and a reminder that clarity is still possible.",
    image: "/icons/Anxious.webp",
    emotionKey: "Anxious",
    details: [
      "Each card reflects your inner world with honesty and care, helping you separate fear from fact.",
      "Anxiety does not define you. There is steadiness waiting beneath the worry.",
    ],
    cards: [
      "nine-of-swords-tarot-card-reading-meaning",
      "the-moon-tarot-card-reading-meaning",
      "temperance-tarot-card-reading-meaning",
    ],
    longForm: `
<p>Anxiety can make the body restless and the mind relentless. Thoughts repeat themselves in circles, each one louder than the last, until it feels as though there is no escape. Tarot does not silence your feelings, but it does offer structure and rhythm. Each card drawn creates a pause. Each spread becomes a container, giving shape to what feels chaotic and showing you that order is still possible.</p>

<p>Begin with a single question. What is happening within me right now. Pull one card and study the details. Notice if you see calm symbols—a steady figure, a balanced cup, or a grounded mountain. These images become anchors. Even when worry tells you everything is collapsing, the cards show that some truths are steady and safe. From there, you can expand into a three-card spread. Ask: What is fear showing me. What is real beneath the fear. What can support me today. The answers do not solve everything, but they give you a direction, and direction is often what the anxious mind craves.</p>

<p>The Nine of Swords is a familiar visitor in anxious readings. It acknowledges the burden of overthinking and encourages compassion rather than shame. The Moon speaks to confusion and illusion, urging you to trust your senses and move slowly. Temperance arrives to remind you that small and balanced choices bring relief: drinking water, resting the body, or breathing with intention. Even the King of Pentacles may appear, showing you the strength of routines that keep you grounded.</p>

<p>Keep your spreads simple when your mind is busy. One card placed on your desk or by your bedside can act as a daily reminder that peace exists, even if only in small moments. Over time, those small moments collect, and anxiety loses its grip. Tarot is not here to scold you for worry. It is here to offer companionship while you find your way back to calm.</p>

<p>When you close a reading, take one promise with you. I will move at my own pace. I will ask for help when I need it. I will give myself patience. These promises may seem small, but they are powerful antidotes to the pressure anxiety creates. With every spread, you remind yourself that beneath the noise, you are steady, capable, and worthy of peace.</p>
`,
  },

  "uncertain love": {
    title: "Tarot for Uncertain Love",
    description:
      "When love feels unclear and the heart is full of mixed signals, tarot helps you slow down and listen to your deeper truth with honesty and care.",
    image: "/icons/Uncertain-Love.webp",
    emotionKey: "Uncertain love",
    details: [
      "Explore the layers of your feelings and the meaning behind silence or mixed signals.",
      "These cards guide you toward clarity and choices that honor your heart.",
    ],
    cards: [
      "two-of-swords-tarot-card-reading-meaning",
      "the-lovers-tarot-card-reading-meaning",
      "seven-of-cups-tarot-card-reading-meaning",
    ],
    longForm: `
<p>Love is rarely simple, and when uncertainty enters the heart, it can feel as though you are standing at a crossroads with no signs to follow. You may question intentions, replay conversations, or wonder whether to step forward or hold back. Tarot offers a patient mirror, showing you what is present in your heart and what boundaries you may need to respect. The cards do not command you to choose quickly. They remind you that clarity arrives when you create space to hear your own truth.</p>

<p>Begin by asking yourself three questions. What am I truly feeling beneath the surface. What truth am I avoiding. What choice would honor my heart today. Draw one card for each and notice the images carefully. The Lovers often appears, not to tell you who to choose, but to remind you that integrity must guide your decision. The Two of Swords may reflect your silence or hesitation, encouraging you to speak honestly. The Seven of Cups warns against mistaking fantasy for reality, asking you to focus on what is tangible and trustworthy.</p>

<p>As you study the spread, notice how your body responds. Warmth or ease suggests alignment. Tightness or heaviness suggests caution. Your body is part of the reading, and its wisdom should be trusted. Write down the truths you uncover, even if they are uncomfortable. You may realize that you already know more than you think, and tarot is simply giving you permission to believe yourself.</p>

<p>Remember that uncertainty in love is not a failure. It is part of learning what your heart values and how you want to be treated. You are allowed to ask questions. You are allowed to expect clarity. You are allowed to walk away from confusion if it does not change. Love grows best where there is honesty, and tarot supports you in cultivating that honesty with courage and compassion.</p>

<p>When you finish your reading, close with an affirmation. I will not betray my values for approval. I will remain open yet clear. I will move at a pace that respects my heart. In this way, tarot becomes not only a tool for love but also a guide toward self respect and deeper trust in your own wisdom.</p>
`,
  },

  confused: {
    title: "Tarot for Confusion",
    description:
      "When choices blur and the way forward feels uncertain, tarot offers patience and focus. These spreads bring clarity and help you find the next right step.",
    image: "/icons/Confused.webp",
    emotionKey: "Confused",
    details: [
      "Each card untangles a knot in your thoughts so you can see what matters first.",
      "Clarity is closer than you think. The cards help you trust yourself again.",
    ],
    cards: [
      "seven-of-cups-tarot-card-reading-meaning",
      "two-of-swords-tarot-card-reading-meaning",
      "the-hanged-man-tarot-card-reading-meaning",
    ],
    longForm: `
<p>Confusion is like walking in fog. You want to move forward, yet every direction looks the same and every choice carries both hope and doubt. In such moments the heart longs for clarity, but the mind keeps spinning. Tarot does not clear the fog all at once. Instead, it lights a lantern that helps you take one step at a time, showing you that progress is possible even when certainty is not.</p>

<p>Begin with three guiding questions. What matters most in this moment. What can I let go of without losing myself. What next action would bring me peace. Draw one card for each. Do not force meaning. Simply describe what you see. If The Hanged Man appears, the message may be to pause and shift your perspective. If the Two of Swords arrives, it may reflect your habit of waiting rather than choosing, and it encourages you to listen to what silence is hiding. The Seven of Cups speaks of many options, reminding you to focus on what feels steady rather than what only sparkles on the surface.</p>

<p>As you write your impressions, notice how your body reacts. If a card stirs calm, trust that calm. If a card brings tension, honor that tension as a warning. Tarot is not only about symbolism. It is about reconnecting with your own signals of truth. This practice transforms confusion from an enemy into a teacher, showing you which paths drain your energy and which paths invite it back.</p>

<p>Sometimes the most healing choice is a small one. You do not need to solve every dilemma in one night. Send one message, cancel one obligation, or take one walk. Confusion often dissolves when you commit to something real and simple. Tarot guides you to that small step, helping you remember that clarity is a process and not a sudden lightning bolt.</p>

<p>Close your reading by writing a short affirmation. Today I will take one step that respects my values. Repeat it before sleep and again when you wake. Over time, small and true actions will carry you out of the fog. Tarot is not here to tell you who you must be. It is here to remind you that you already carry a compass, and that compass is waiting to be trusted.</p>
`,
  },

  lonely: {
    title: "Tarot for Loneliness",
    description:
      "Loneliness is more than being without people. It is the ache of feeling unseen and unheard. Tarot reminds you that your presence matters and your light is still shining.",
    image: "/icons/Lonely.webp",
    emotionKey: "Lonely",
    details: [
      "Each card meets you with quiet warmth and reminds you of your worth.",
      "You are not truly alone. Your spirit belongs, and the world needs your light.",
    ],
    cards: [
      "five-of-pentacles-tarot-card-reading-meaning",
      "the-hermit-tarot-card-reading-meaning",
      "four-of-cups-tarot-card-reading-meaning",
    ],
    longForm: `
<p>Loneliness does not always come from being alone. It often comes from feeling invisible, from carrying stories no one else has asked to hear, from wishing someone would notice the weight you hold in silence. Tarot cannot place someone in the room with you, but it can remind you that your spirit is seen, that your worth is intact, and that connection is still possible.</p>

<p>Start by asking three gentle questions. What needs to be witnessed. What support is already near me. What step would help me feel less alone today. The Hermit often appears, showing that solitude is not emptiness but a sacred chance to know yourself deeply. The Five of Pentacles may reflect the fear of being left out, while also hinting that a door is open nearby if you are willing to notice. The Four of Cups arrives when the heart feels tired and reminds you that new connection may be waiting in forms you have overlooked.</p>

<p>Tarot can also encourage you to turn loneliness into presence. Light a candle. Place one card in front of you. Let that image act as a companion for the evening. Describe the figure, the colors, the scene, as if you were talking to a friend. This simple ritual reminds your body that you are not abandoned. You are in conversation, even if only with yourself and the symbols that reflect your soul.</p>

<p>Consider small ways to reconnect. Reach out to one trusted person. Step outside and greet the natural world. Write a letter, even if you do not send it. The action does not have to be grand. What matters is that you create openings where connection can enter. Each small act tells your spirit: I am part of life, I am part of others, and I matter here.</p>

<p>Close your reading with an affirmation. I am seen. I am worthy. I am part of the world. Repeat these lines until they feel like gentle truth. Over time, loneliness loses its sharpness when you practice reminding yourself that you belong. Tarot offers the mirror, but you are the one who chooses to see your own light in it.</p>
`,
  },

  hopeless: {
    title: "Tarot for Hopelessness",
    description:
      "When hope feels distant and the future looks heavy, tarot offers quiet light. These spreads help you remember strength and guide you toward renewal.",
    image: "/icons/Hopeless.webp",
    emotionKey: "Hopeless",
    details: [
      "These cards remind you that strength still lives within you, even when the world feels dark.",
      "They help you take one step at a time toward brighter ground and a softer tomorrow.",
    ],
    cards: [
      "ten-of-swords-tarot-card-reading-meaning",
      "the-star-tarot-card-reading-meaning",
      "strength-tarot-card-reading-meaning",
    ],
    longForm: `
<p>Hopelessness can make the future look like a wall with no door. Each day feels heavy, and it becomes difficult to believe that life can ever be different. Tarot does not deny this pain. It does not try to cover it with false cheer. Instead, it sits with you in honesty and offers a small light to guide you forward. That light may not erase the night, but it is enough to remind you that dawn is possible.</p>

<p>Begin with three positions. What has reached its end. What is still alive within me. What step would bring renewal today. The Ten of Swords often appears to mark the closure of a painful chapter. Though it looks harsh, it carries a hidden blessing: the worst is behind you, and there is space for healing to begin. The Star shines after turmoil, reminding you that calm and faith can return slowly, like the first glimmers after a storm. Strength appears to affirm that quiet endurance and compassion for yourself are powerful forces in rebuilding.</p>

<p>As you study the cards, notice the emotions they stir. A single spark of relief is worth honoring. A single reminder of resilience is worth writing down. Tarot helps you hold on to these fragments until they become enough to carry you. Do not expect yourself to leap into joy overnight. Let hope return gently, through small choices that honor your life. Eat something nourishing. Step into sunlight. Speak one kind sentence to yourself. These are not trivial. They are proof that you are choosing life in the midst of difficulty.</p>

<p>Sometimes hopelessness lingers because you believe you must do everything alone. Tarot encourages you to ask for help. Friends, professionals, or even strangers can carry a spark for you when yours is faint. The cards remind you that connection is part of the path to healing, and that reaching out is an act of courage, not weakness.</p>

<p>Close your reading with a statement of faith, even if it feels fragile. I am still here. I am open to change. I am willing to see the next light. These words may not fix everything, but they plant a seed. With each repetition, the seed grows. Tarot does not promise perfection. It promises companionship as you take step after step back toward hope.</p>
`,
  },

  guilty: {
    title: "Tarot for Guilt",
    description:
      "Guilt is a silence that lingers in the heart. Tarot helps you reflect, forgive, and transform regret into understanding and growth.",
    image: "/icons/Guilty.webp",
    emotionKey: "Guilty",
    details: [
      "These cards uncover the truth beneath shame and turn self blame into growth.",
      "They guide you toward compassion, for others and most importantly for yourself.",
    ],
    cards: [
      "justice-tarot-card-reading-meaning",
      "the-devil-tarot-card-reading-meaning",
      "judgement-tarot-card-reading-meaning",
    ],
    longForm: `
<p>Guilt is one of the heaviest emotions to carry. It settles in the chest like a stone, reminding you of choices you wish you could change or words you wish you had spoken differently. Sometimes guilt is deserved, pointing to a moment when you fell short of your values. Other times it is inherited, placed on your shoulders by expectations or stories that were never yours to begin with. Tarot can help you sort through the weight, showing what belongs to you and what can be released.</p>

<p>Begin by asking three questions. What guilt is truly mine to carry. What lesson is hidden within this guilt. What step forward would honor the lesson. Justice often appears to remind you that fairness must apply to yourself as well as others. The Devil may reveal chains of shame, showing that you are more bound by judgment than by truth. Judgement appears as a call to awaken, to rise from cycles of self blame and answer a higher call to integrity.</p>

<p>If your guilt comes from harm you have caused, tarot may guide you toward repair. A heartfelt apology, a change in behavior, or a promise you can keep are all ways of transforming regret into growth. If your guilt is unearned, the cards may point to its source. A family expectation, an old belief, or another’s harsh words can leave stains on the heart that are not yours to clean. Releasing this false guilt is not denial. It is reclaiming your right to live freely and honestly.</p>

<p>Writing can help. After your spread, write down what belongs to you and what does not. Burn or tear the page of what is not yours. Keep the part that teaches you and commit to acting on it. This simple ritual creates space for forgiveness, which does not erase the past but frees your future. Tarot reminds you that every card is part of a journey. No one card defines the whole deck, and no one mistake defines your life.</p>

<p>Close your reading with an affirmation: I am willing to be honest. I am willing to grow. I will act with care. Let these words echo louder than the silence of guilt. Over time, compassion for yourself becomes the bridge that transforms regret into wisdom. Guilt, when faced with love, can become not a chain but a guide toward a more truthful way of living.</p>
`,
  },

  betrayed: {
    title: "Tarot for Betrayal",
    description:
      "Betrayal shakes trust and wounds the heart. Tarot honors your pain and helps you rebuild with stronger boundaries and renewed strength.",
    image: "/icons/Betrayed.webp",
    emotionKey: "Betrayed",
    details: [
      "These cards validate your pain and awaken intuition that protects your peace.",
      "From broken trust you can rise with clear vision and stronger roots.",
    ],
    cards: [
      "seven-of-swords-tarot-card-reading-meaning",
      "three-of-swords-tarot-card-reading-meaning",
      "the-tower-tarot-card-reading-meaning",
    ],
    longForm: `
<p>Betrayal cuts deeply because it breaks into the place where you felt safest. Trust, once broken, leaves behind not only sadness but also doubt in your ability to trust again. Tarot does not minimize this wound. It acknowledges the rupture and sits with you as you begin to rebuild. Each card becomes both a witness to your pain and a guide toward reclaiming your power.</p>

<p>Start with three questions. What was broken. What remains whole. What boundary will protect me today. The Seven of Swords often points to deception, reminding you to stay alert and wise. The Three of Swords validates your grief, showing that it is natural to hurt deeply when promises are broken. The Tower represents the collapse of false foundations, urging you to clear away what cannot be trusted so you can build on ground that is honest and strong.</p>

<p>As you read, give yourself permission to feel anger and sorrow fully. Avoid the pressure to forgive before you are ready. Forgiveness is not forgetting. It is choosing not to carry poison in your own veins. Boundaries are a form of healing. Make a list of what safety looks like to you. Make another list of red flags that signal risk. Keep both nearby as reminders that you are not powerless. You are learning to protect yourself with clarity.</p>

<p>If the cards suggest repair, remember that trust can only be rebuilt with consistent action over time. Words alone are not enough. Actions that align with truth are the only proof worth holding. If repair is not possible, allow yourself to grieve what was lost and honor the space you are now free to create. Your strength is not measured by how quickly you “move on” but by how deeply you choose honesty from this point forward.</p>

<p>To close your reading, place a card that symbolizes strength or clarity where you can see it each day. Let it remind you that trust in yourself is returning. Betrayal does not define your worth. It defines the limits of someone else’s integrity. You remain whole, and your roots are growing stronger with every choice to honor yourself.</p>
`,
  },

  insecure: {
    title: "Tarot for Insecurity",
    description:
      "When self doubt grows loud, tarot reminds you of your natural worth. These spreads quiet the inner critic and reconnect you with confidence.",
    image: "/icons/Insecure.webp",
    emotionKey: "Insecure",
    details: [
      "These cards soften the voice of the critic and help you stand in your natural power.",
      "You are already enough. The reading reminds you why.",
    ],
    cards: [
      "five-of-pentacles-tarot-card-reading-meaning",
      "nine-of-swords-tarot-card-reading-meaning",
      "the-empress-tarot-card-reading-meaning",
    ],
    longForm: `
<p>Insecurity often begins as a whisper. A quiet thought that questions your worth, your appearance, your abilities. Left unchecked, the whisper grows until it drowns out your confidence. Tarot cannot erase every doubt, but it can help you hear another voice—the voice of your truth. Each card reminds you that you are not broken or lacking. You are already whole, and you are learning to see that wholeness clearly.</p>

<p>Begin with three questions. What story is my insecurity repeating. What truth supports me. What action can strengthen my confidence today. The Five of Pentacles often appears when you feel excluded or not enough, reminding you to notice the doors that are already open. The Nine of Swords speaks of worry and sleepless nights, urging you to meet those fears with kindness rather than judgment. The Empress represents self worth, creativity, and the abundance already within you, inviting you to treat yourself as something precious and deserving.</p>

<p>Use your cards as daily reminders. Place one on your desk or mirror each morning. Let its image interrupt the critic’s voice with a gentler message. Write down three qualities you admire in yourself, even if they feel small. Choose one action each day that reflects those qualities: send a kind note, finish a task, or care for your body. Confidence grows from practice, not pressure.</p>

<p>Tarot also helps you recognize that insecurity is not an enemy to fight but a signal to listen more deeply. What part of you is asking for reassurance. What wound is asking to be healed. By naming these parts with compassion, you reduce their power over you. You become the caretaker of your inner self instead of the critic.</p>

<p>Close your reading with gratitude for your body and spirit. Stand tall, breathe deeply, and say: I am enough as I am. I am growing every day. I choose to believe in my worth. With time, these words sink in and replace the whisper of doubt with the steady rhythm of truth.</p>
`,
  },

  overwhelmed: {
    title: "Tarot for Overwhelm",
    description:
      "When everything feels like too much, tarot helps you pause, breathe, and find clarity. These spreads guide you toward rest and focus.",
    image: "/icons/Overwhelmed.webp",
    emotionKey: "Overwhelmed",
    details: [
      "Each card brings calm clarity so you can focus on what truly matters today.",
      "Step gently out of the noise. You are allowed to rest and realign with your own pace.",
    ],
    cards: [
      "ten-of-wands-tarot-card-reading-meaning",
      "seven-of-cups-tarot-card-reading-meaning",
      "four-of-swords-tarot-card-reading-meaning",
    ],
    longForm: `
<p>Overwhelm can arrive quietly or all at once. A list of tasks grows too long, obligations multiply, and the mind begins to scatter in many directions at once. In these moments, even rest feels unreachable because the body is restless and the spirit is heavy. Tarot does not demand that you push harder. It invites you to step back, breathe, and find clarity in simplicity. The spreads become small lanterns, reminding you that not everything must be carried today.</p>

<p>Begin by asking three guiding questions. What truly needs my attention right now. What can I release or delay. What would restore me if I chose it. Draw one card for each. The Ten of Wands often appears when you are carrying too much without support. Its message is to set some weight down. The Seven of Cups shows the distraction of too many choices and asks you to choose one priority. The Four of Swords represents rest, reminding you that restoration is not wasted time but necessary preparation for what comes next.</p>

<p>As you look at your cards, notice which images make you feel tense and which bring relief. Your body often knows the answer before your mind admits it. A task that tightens your chest may not need to be done today. A symbol that calms you points toward where your focus should be. Tarot helps you separate urgency from importance, teaching you that everything does not carry the same weight.</p>

<p>Practice creating a small list from your reading. Write down only three lines: one thing to do, one thing to delay, and one thing that will restore you. Honor this list as sacred. It may feel strange at first to allow yourself such simplicity, but the act of choosing less is an act of trust. It says: I do not need to do everything to be worthy. I can move slowly, and life will still unfold.</p>

<p>If you find yourself overwhelmed by emotions rather than tasks, the cards can still guide you. They remind you that you are allowed to pause, to process, and to take life in smaller pieces. Overwhelm thrives on the belief that everything must happen now. Tarot counters that belief with images of patience, balance, and rest.</p>

<p>Close your reading with an affirmation: I am allowed to move at a human pace. I release what I cannot hold today. I welcome calm into my body and mind. These words may feel simple, but repeated often they become powerful medicine. With practice, overwhelm loses its grip, and you return to your own rhythm.</p>
`,
  },

  other: {
    title: "Tarot for Other Emotions",
    description:
      "Not every feeling fits a single name. Tarot welcomes complex or unspoken emotions, offering a space where your truth can be honored.",
    image: "/icons/Other.webp",
    emotionKey: "Other",
    details: [
      "Speak your truth in your own words. The tarot will meet you exactly where you are.",
      "Your feelings are sacred, even when they cannot be easily defined or labeled.",
    ],
    cards: [
      "wheel-of-fortune-tarot-card-reading-meaning",
      "the-fool-tarot-card-reading-meaning",
      "the-high-priestess-tarot-card-reading-meaning",
    ],
    longForm: `
<p>Some emotions do not come with names. They rise as a mix of many feelings at once—grief tangled with joy, fear wrapped in excitement, longing mixed with relief. Language struggles to describe them, and yet they shape the way you breathe, move, and live. Tarot offers a place where even unnamed emotions can be honored. You do not need the perfect word for the cards to listen. The symbols will meet you where you are, speaking to the truth beneath the surface.</p>

<p>Begin by writing a few sentences in your own words. Do not worry about polish or precision. Simply let the page carry what your heart is trying to say. Then ask the cards to respond. Draw one card for each line you wrote, and notice how the images reflect your feelings back to you. The Fool may appear, showing the freedom of beginning again. The High Priestess honors intuition and the mysteries within you, reminding you that not everything must be explained to be real. The Wheel of Fortune reflects the cycles of life, assuring you that change is constant and that emotions, like seasons, will shift with time.</p>

<p>Tarot allows you to create space for emotions that feel contradictory. You can grieve and celebrate in the same breath. You can be afraid and hopeful at once. The cards do not ask you to choose one or the other. They simply mirror what is, making the complexity feel less like chaos and more like a living truth. This practice can be deeply healing, because it validates what often goes unspoken.</p>

<p>Consider making art or movement part of your reading. Sketch the images you draw. Dance to the rhythm of the cards. Write a poem that weaves their symbols into your own story. When emotions cannot be captured by plain words, creative expression helps them find their voice. Tarot opens that door and encourages you to step through it without judgment.</p>

<p>Close your reading with gratitude for the mystery of being human. Say to yourself: I do not need perfect words to deserve care. My feelings are valid even when they cannot be defined. My soul is still understood. These affirmations free you from the pressure of labels and open the way to self compassion. Tarot assures you that no matter how complex or strange your emotions may feel, they are worthy of recognition, and so are you.</p>
`,
  },

};

const suitToPath = {
  "Major Arcana": "/tarot/major-arcana-tarot-suit-cards-meaning",
  Wands: "/tarot/wands-tarot-suit-cards-meaning",
  Cups: "/tarot/cups-tarot-suit-cards-meaning",
  Swords: "/tarot/swords-tarot-suit-cards-meaning",
  Pentacles: "/tarot/pentacles-tarot-suit-cards-meaning",
};

// ==== Emotion color themes (keys must match your slug) ====
const bgThemes = {
  default: {
    radial: "rgba(164,120,255,0.25)",
    top: "#3b1d5a",
    l1: "#2a1948",
    l2: "#5a2a72",
    btnFrom: "#03779B",
    btnTo: "#005C83",
    accent: "#cbb7ea",
  },
  "heart broken": {
    radial: "rgba(255,120,160,0.25)",
    top: "#5a1a2f",
    l1: "#7c2642",
    l2: "#a23b59",
    btnFrom: "#b33a61",
    btnTo: "#5a1a2f",
    accent: "#f0a3b7",
  },
  anxious: {
    radial: "rgba(120,180,255,0.25)",
    top: "#12375a",
    l1: "#0e4f73",
    l2: "#0b6c86",
    btnFrom: "#0C4B84",
    btnTo: "#03779B",
    accent: "#9fd0ff",
  },
  "uncertain love": {
    radial: "rgba(255,190,140,0.22)",
    top: "#5a3a1d",
    l1: "#6a3f2b",
    l2: "#8b5a3a",
    btnFrom: "#c77b3b",
    btnTo: "#6a3f2b",
    accent: "#ffd8b0",
  },
  confused: {
    radial: "rgba(200,180,255,0.25)",
    top: "#2b1f5a",
    l1: "#3f2c74",
    l2: "#5a3c8f",
    btnFrom: "#5440a3",
    btnTo: "#2b1f5a",
    accent: "#d5c9ff",
  },
  lonely: {
    radial: "rgba(160,210,255,0.22)",
    top: "#1d3557",
    l1: "#24476f",
    l2: "#3a6ea5",
    btnFrom: "#3a6ea5",
    btnTo: "#1d3557",
    accent: "#b8d9ff",
  },
  hopeless: {
    radial: "rgba(200,200,200,0.22)",
    top: "#2a2a2a",
    l1: "#30343f",
    l2: "#545e75",
    btnFrom: "#4b5563",
    btnTo: "#2a2a2a",
    accent: "#d6dde6",
  },
  guilty: {
    radial: "rgba(255,180,160,0.22)",
    top: "#3f1e1a",
    l1: "#53302a",
    l2: "#6a4037",
    btnFrom: "#a45a49",
    btnTo: "#3f1e1a",
    accent: "#f3c8b9",
  },
  betrayed: {
    radial: "rgba(255,150,170,0.24)",
    top: "#471c2a",
    l1: "#5a2635",
    l2: "#7a394b",
    btnFrom: "#c24b67",
    btnTo: "#471c2a",
    accent: "#f2a6b9",
  },
  insecure: {
    radial: "rgba(190,230,255,0.24)",
    top: "#153b45",
    l1: "#1c4f5a",
    l2: "#2b6b75",
    btnFrom: "#2b9cb3",
    btnTo: "#14656a",
    accent: "#bfefff",
  },
  overwhelmed: {
    radial: "rgba(255,210,120,0.22)",
    top: "#5a421a",
    l1: "#6b5126",
    l2: "#896a35",
    btnFrom: "#c78a2f",
    btnTo: "#5a421a",
    accent: "#ffdca3",
  },
  other: {
    radial: "rgba(190,190,255,0.25)",
    top: "#2e2a5a",
    l1: "#3a3674",
    l2: "#564f9a",
    btnFrom: "#5f58c6",
    btnTo: "#2e2a5a",
    accent: "#d1ccff",
  },
};

// Body portal
function BodyPortal({ children }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted ? createPortal(children, document.body) : null;
}

export default function EmotionPage() {
  const { emotionSlug } = useParams();

  // Normalize route slug to a simple key we can match
  const slug =
    emotionSlug?.toLowerCase().replace("tarot-reading-for-", "").replace(/-/g, " ").trim() || "";

  // Robust meta finder (no crash if something is missing)
  const meta =
    Object.values(emotionMeta).find(
      (e) => typeof e?.emotionKey === "string" && e.emotionKey.toLowerCase().trim() === slug
    ) || null;

  const theme = bgThemes[slug] || bgThemes.default;
  const [showPopup, setShowPopup] = useState(false);
  const [popupStep, setPopupStep] = useState(1); // 1: select spread, 2: enter whisper, 3: show reading

  // Scroll freeze when popup is open
  const scrollYRef = useRef(0);
  useEffect(() => {
    if (showPopup) {
      scrollYRef.current =
        window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollYRef.current}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";
    } else {
      const y = Math.abs(parseInt(document.body.style.top || "0", 10)) || 0;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      if (y) window.scrollTo(0, y);
    }

    return () => {
      if (document.body.style.position === "fixed") {
        const y = Math.abs(parseInt(document.body.style.top || "0", 10)) || 0;
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        document.body.style.right = "";
        document.body.style.width = "";
        document.body.style.overflow = "";
        if (y) window.scrollTo(0, y);
      }
    };
  }, [showPopup]);

  const [selectedSpread, setSelectedSpread] = useState(null);
  const [customWhisper, setCustomWhisper] = useState("");
  const [readingStarted, setReadingStarted] = useState(false);
  const [mockReading, setMockReading] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldownMessage, setCooldownMessage] = useState("");
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [lastReadingTime, setLastReadingTime] = useState(null);
  const [showCopiedToast, setShowCopiedToast] = useState(false);
  const [showDonation, setShowDonation] = useState(false);
  const [customAmount, setCustomAmount] = useState("");

  // Update popup step when selections change
  useEffect(() => {
    if (showPopup && !readingStarted) {
      if (selectedSpread) {
        setPopupStep(2); // Move to whisper input after selecting spread
      } else {
        setPopupStep(1); // Still selecting spread
      }
    }
  }, [selectedSpread, showPopup, readingStarted]);

  // Cooldown timer effect
  useEffect(() => {
    let interval;
    if (cooldownSeconds > 0) {
      interval = setInterval(() => {
        setCooldownSeconds((prev) => prev - 1);
      }, 1000);
    } else if (cooldownSeconds === 0 && cooldownMessage) {
      setCooldownMessage("");
    }

    return () => clearInterval(interval);
  }, [cooldownSeconds, cooldownMessage]);

  // ---- SEO helpers (EmotionPage) ----
  if (!meta) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#064382]">
        <div className="text-center p-10 rounded-2xl bg-white/10 backdrop-blur-lg border border-white/10">
          <h2 className="text-xl font-semibold text-white">Emotion not found</h2>
          <p className="text-sm mt-2 text-white/70">Check the URL or select another emotion from the menu.</p>
        </div>
      </div>
    );
  }

  const siteUrl = "https://tarotfate.com";
  const emotionName = meta.emotionKey || "Tarot";
  const pageUrl = `${siteUrl}/emotions/${emotionSlug}`;
  const ogImage = `${siteUrl}${meta.image || "/icons/og-cover.jpg"}`;
  const orgLogo = `${siteUrl}/icons/logo.webp`;          // organization logo (square)

  const descBase =
    emotionName === "Other"
      ? "Name your feeling and get a tailored tarot reading — choose 1, 3, or 5 cards for clear meanings and next steps. Free, private, instant."
      : `${emotionName} tarot reading — pick 1, 3, or 5 cards for clear meanings, guidance, and next steps. Free, private, instant.`;
  const metaDescription = descBase.length > 158 ? `${descBase.slice(0, 155)}…` : descBase;

  // Page-specific keywords (Seo will append mega keywords when includeMega={true})
  const pageKeywords = [
    "tarot",
    "tarot reading",
    "free tarot",
    "online tarot",
    `${emotionName} tarot`,
    `${emotionName} reading`,
    `tarot for ${emotionName}`,
    "one card reading",
    "three card spread",
    "five card spread",
    "upright meanings",
    "reversed meanings",
    "major arcana",
    "minor arcana",
    "wands",
    "cups",
    "swords",
    "pentacles",
  ]
    .join(", ")
    .trim();

  // JSON-LD schema for this emotion page
  const schemaGraph = {
    "@context": "https://schema.org",
    "@graph": [
      { "@id": `${siteUrl}#org` },
      { "@id": `${siteUrl}#website` },
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: `${meta.title} — Free Online Tarot Reading`,
        description: metaDescription,
        isPartOf: { "@id": `${siteUrl}#website` },
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: ogImage,
          width: 1200,
          height: 630,
        },
        inLanguage: "en",
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
          { "@type": "ListItem", position: 2, name: meta.emotionKey || "Emotion", item: pageUrl },
        ],
      },
    ],
  };

  const spreads = [
    { value: 1, title: "The Whisper", icon: "Spread1.png", desc: "A single card to gently reflect your emotional state in this moment." },
    { value: 3, title: "The Thread", icon: "Spread3.png", desc: "A linear 3 cards spread that follows your journey: where you've been, where you are, where you're headed." },
    { value: 5, title: "The Mirror", icon: "Spread5.png", desc: "Five cards spread that dive deeper into the emotional terrain and guide your next move." },
  ];

  const isOtherSelected = meta?.emotionKey === "Other";
  const canProceed = !!selectedSpread && (!isOtherSelected || customWhisper.trim().length > 0);

  function formatReading(raw = "", spread) {
    let t = String(raw).replace(/\r\n/g, "\n");

    // 0) Remove numeric bullets like "1. ", "2) ", "3 – "
    t = t.replace(/^\s*\d+\s*[.)–—-]\s+/gm, "");

    // 1) Remove standalone horizontal rules like '---' or '———'
    t = t.replace(/^\s*[-—]{3,}\s*$/gm, "");

    // 2) Strip stray bullet dashes at line starts
    t = t.replace(/^\s*[-–—]\s+/gm, "");

    // 3) Tidy whitespace
    t = t.replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n");

    // 4) Standardize "Card Drawn/Pulled" -> "Card: X"
    t = t.replace(
      /^\s*\*{0,2}\s*(Card\s+(?:Drawn|Pulled))\s*:?\s*(.+?)\s*\*{0,2}\s*$/gim,
      "<strong>Card</strong>: $2"
    );

    // 5) 3-card normalization
    if (spread === 3) {
      t = t
        .replace(/^\s*\*{0,2}Past\*{0,2}\s*[:–—-]\s*(.+)$/gim, "<strong>Card 1</strong>: Past — <strong>$1</strong>")
        .replace(/^\s*\*{0,2}Present\*{0,2}\s*[:–—-]\s*(.+)$/gim, "<strong>Card 2</strong>: Present — <strong>$1</strong>")
        .replace(/^\s*\*{0,2}Future\*{0,2}\s*[:–—-]\s*(.+)$/gim, "<strong>Card 3</strong>: Future — <strong>$1</strong>")
        .replace(/^\s*\*{0,2}Card\s*1\*{0,2}[^:\n]*[:–—-]\s*(.+?)$/gim, "<strong>Card 1</strong>: Past — <strong>$1</strong>")
        .replace(/^\s*\*{0,2}Card\s*2\*{0,2}[^:\n]*[:–—-]\s*(.+?)$/gim, "<strong>Card 2</strong>: Present — <strong>$1</strong>")
        .replace(/^\s*\*{0,2}Card\s*3\*{0,2}[^:\n]*[:–—-]\s*(.+?)$/gim, "<strong>Card 3</strong>: Future — <strong>$1</strong>");
    }

    // 6) 5-card normalization
    if (spread === 5) {
      t = t
        .replace(/^\s*\*{0,2}Card\s*1\*{0,2}[^:\n]*[:–—-]\s*(.+?)$/gim, "<strong>Card 1</strong>: The Heart of the Emotion — <strong>$1</strong>")
        .replace(/^\s*\*{0,2}Card\s*2\*{0,2}[^:\n]*[:–—-]\s*(.+?)$/gim, "<strong>Card 2</strong>: The Shadow — <strong>$1</strong>")
        .replace(/^\s*\*{0,2}Card\s*3\*{0,2}[^:\n]*[:–—-]\s*(.+?)$/gim, "<strong>Card 3</strong>: The Shift — <strong>$1</strong>")
        .replace(/^\s*\*{0,2}Card\s*4\*{0,2}[^:\n]*[:–—-]\s*(.+?)$/gim, "<strong>Card 4</strong>: The Strength — <strong>$1</strong>")
        .replace(/^\s*\*{0,2}Card\s*5\*{0,2}[^:\n]*[:–—-]\s*(.+?)$/gim, "<strong>Card 5</strong>: The Path Forward — <strong>$1</strong>")
        .replace(/^\s*\*{0,2}The Heart of the Emotion\*{0,2}\s*[:–—-]\s*(.+)$/gim, "<strong>Card 1</strong>: The Heart of the Emotion — <strong>$1</strong>")
        .replace(/^\s*\*{0,2}The Shadow\*{0,2}\s*[:–—-]\s*(.+)$/gim, "<strong>Card 2</strong>: The Shadow — <strong>$1</strong>")
        .replace(/^\s*\*{0,2}The Shift\*{0,2}\s*[:–—-]\s*(.+)$/gim, "<strong>Card 3</strong>: The Shift — <strong>$1</strong>")
        .replace(/^\s*\*{0,2}The Strength\*{0,2}\s*[:–—-]\s*(.+)$/gim, "<strong>Card 4</strong>: The Strength — <strong>$1</strong>")
        .replace(/^\s*\*{0,2}The Path Forward\*{0,2}\s*[:–—-]\s*(.+)$/gim, "<strong>Card 5</strong>: The Path Forward — <strong>$1</strong>");
    }

    // 7) 1-card: clean "Card: X"
    if (spread === 1) {
      t = t.replace(/^\s*\*{0,2}Card\*{0,2}\s*:?\s*(.+?)\s*$/gim, "<strong>Card</strong>: $1");
    }

    // 8) Normalize "Wisdom Whisper" heading
    t = t.replace(/^\s*\*{0,2}Wisdom Whisper\*{0,2}\s*:?\s*$/gim, "<strong>Wisdom Whisper</strong><br/>");

    // 9) Markdown bold/italics
    t = t.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    t = t.replace(/(^|[^*])\*([^*\n]+)\*(?=[^*]|$)/g, "$1<em>$2</em>");

    // 10) Uppercase-ish labels like "Opening:"
    t = t.replace(/^\s*([A-Z][A-Za-z ]{2,})\s*:\s*$/gm, "<strong>$1</strong><br/>");

    // 11) Honor hard line breaks (two spaces + newline)
    t = t.replace(/ {2}\n/g, "<br/>\n");

    return t;
  }

  // Safer markdown -> HTML (simple bold/italic only)
  const formatTextWithMarkdown = (text) => {
    const bold = text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    const italic = bold.replace(/\*(.+?)\*/g, "<em>$1</em>");
    return italic;
  };

  const handleBackButton = () => {
    if (popupStep === 3) {
      goBackToStep1();
    } else if (popupStep === 2) {
      setPopupStep(1);
      setSelectedSpread(null);
    }
  };

  const goBackToStep1 = () => {
    setPopupStep(1);
    setSelectedSpread(null);
    setReadingStarted(false);
  };

  const resetPopup = () => {
    setSelectedSpread(null);
    setCustomWhisper("");
    setReadingStarted(false);
    setMockReading("");
    setLoading(false);
    setShowCopiedToast(false);
    setShowDonation(false);
    setShowPopup(false);
    setCooldownMessage("");
    setCooldownSeconds(0);
    setCustomAmount("");
    setPopupStep(1);
  };

  const handleReadTarot = async () => {
    const now = Date.now();

    if (!selectedSpread) {
      setCooldownMessage("Select a spread first.");
      setTimeout(() => setCooldownMessage(""), 2000);
      return;
    }
    if (isOtherSelected && !customWhisper.trim()) {
      setCooldownMessage("Tell me what you're feeling under 'Other' to proceed.");
      setTimeout(() => setCooldownMessage(""), 2500);
      return;
    }
    if (lastReadingTime && now - lastReadingTime < 60000) {
      const secondsLeft = Math.ceil((60000 - (now - lastReadingTime)) / 1000);
      setCooldownSeconds(secondsLeft);
      setCooldownMessage(
        `(Please wait ${secondsLeft} seconds) The tarot speaks in whispers, not in haste. Let its magic settle before seeking again.`
      );
      return;
    }

    setLastReadingTime(now);
    setReadingStarted(true);
    setPopupStep(3);
    setCooldownMessage("");
    setCooldownSeconds(0);
    setLoading(true);

    try {
      const payload = {
        emotion: meta?.emotionKey,
        spread: selectedSpread,
        whisper: customWhisper || "",
      };

      const res = await fetch(`${API_BASE}/api/tarot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const raw = await res.text();
      let data;
      try {
        data = JSON.parse(raw);
      } catch {
        /* ignore non-JSON */
      }

      if (!res.ok) {
        const msg = data?.error || `Server error ${res.status}`;
        setMockReading(`Error: ${msg}`);
        return;
      }

      if (!data?.reading) {
        setMockReading("No reading returned from server.");
        return;
      }

      setMockReading(data.reading);
    } catch (e) {
      setMockReading(
        `The mystic veil could not be lifted to feel your emotion at this moment. Please try again shortly.`
      );
    } finally {
      setLoading(false);
    }
  };

  const donationAmounts = [5, 10, 20, 50, 100];

  return (
    <div
      className="min-h-screen relative"
      style={{
        background:
          `radial-gradient(1200px 1000px at 50% -10%, ${theme.radial} 10%, ${theme.top} 35%), ` +
          `linear-gradient(180deg, ${theme.l1} 40%, ${theme.l2} 50%)`,
      }}
    >
      {/* SEO */}
      <Seo
        title={`${meta.title} — Meanings & Emotion-Based Tarot Reading`}
        description={metaDescription}
        url={pageUrl}
        image={ogImage}
        schema={schemaGraph}
        keywords={pageKeywords}
        includeMega={true}
      />

      <main className="relative z-10 max-w-3xl mx-auto px-6 py-8 text-white/90 font-[Cormorant_Garamond]">
        {/* Emotion Icon */}
        <div className="flex justify-center">
          <div className="relative p-[3px] rounded-full bg-gradient-to-br from-[#FDA509]/80 via-white/20 to-[#821E3C]">
            <div className="rounded-full bg-[#006A93]/30 backdrop-blur-xl p-2">
              <img
                src={meta.image}
                alt={emotionSlug}
                className="w-20 h-20 sm:w-24 sm:h-24 object-contain select-none pointer-events-none"
                onContextMenu={(e) => e.preventDefault()}
                draggable="false"
              />
            </div>
          </div>
        </div>

        {/* Separator */}
        <div className="mt-4 mb-6 flex items-center justify-center">
          <div className="h-px w-1/5 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
          <span className="mx-4 text-white/70 text-xl">✦</span>
          <div className="h-px w-1/5 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
        </div>

        {/* Title & description */}
        <h1 className="text-xl sm:text-2xl tracking-wide text-center font-bold">
          {meta.title.replace("Tarot for ", "")}
        </h1>
        <p className="mt-3 text-base sm:text-lg text-center max-w-2xl mx-auto text-white/85">
          {meta.description}
        </p>

        {/* Frosted content block */}
        <section className="mt-8 rounded-lg border border-white/45 bg-gradient-to-b from-[#03779B]/90 via-[#0C4B84]/100 to-[#2b9cb3]/10 backdrop-blur-sm shadow-[0_3px_10px_rgba(0,0,0,0.45)]">
          <div className="p-6 sm:p-8">
            <div className="space-y-2 text-white/85 text-base leading-relaxed max-w-2xl mx-auto text-center">
              {meta.details.map((line, idx) => (
                <p key={idx}>{line}</p>
              ))}
            </div>

            <div className="flex justify-center mt-8">
              <button
                onClick={() => setShowPopup(true)}
                className="px-7 py-2.5 rounded-full font-semibold border border-white/20 text-white shadow-xl shadow-[0_10px_20px_rgba(0,0,0,0.45)
                           hover:shadow-xl transition-transform duration-100 hover:-translate-y-0.5"
                style={{ backgroundImage: `linear-gradient(180deg, ${theme.btnFrom}, ${theme.btnTo})` }}
              >
                Read My Tarot
              </button>
            </div>
            
          </div>
        </section>
        {/* Long-form emotion content */}
{meta.longForm?.trim() && (
  <div className="mt-8 text-white/90 leading-relaxed text-base sm:text-lg prose prose-invert max-w-none">
    <div dangerouslySetInnerHTML={{ __html: meta.longForm }} />
  </div>
)}

        {/* Featured cards */}
        {meta.cards && (
          <section className="mt-10">
            <div className="my-8 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
            <h2 className="text-center text-xl font-semibold text-white/95">
              Common '{meta.emotionKey.toLowerCase()}' cards
            </h2>

            <div className="mt-6 flex flex-wrap justify-center gap-6">
              {meta.cards.map((slug) => {
                const card = cardData[slug];
                if (!card) return null;
                return (
                  <Link to={`/tarot-card/${card.slug}`} key={card.slug} className="w-[120px] group">
                    <div className="rounded-xl overflow-hidden border border-white/15 bg-white/5 backdrop-blur-md shadow-[0_6px_20px_rgba(0,0,0,0.35)] group-hover:shadow-[0_2px_8px_rgba(255,255,255,0.8)] transition">
                      <img src={card.image} alt={card.name} className="w-full h-auto" />
                    </div>
                    <p className="mt-2 text-center text-sm font-medium text-white/90 group-hover:text-white">
                      {card.name}
                    </p>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Suit Navigation */}
        <div className="my-10 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
        <div className="text-center space-y-4 mb-6">
          <div className="flex flex-wrap justify-center gap-3 mt-1">
            {[
              { k: "Major Arcana", label: "✦ Major Arcana" },
              { k: "Wands", label: "✦ Wands" },
              { k: "Cups", label: "✦ Cups" },
              { k: "Swords", label: "✦ Swords" },
              { k: "Pentacles", label: "✦ Pentacles" },
              { k: "All", label: "✦ All Cards", path: "/tarot-cards" },
            ].map((s) => {
              const to = s.path || suitToPath[s.k];
              return (
                <Link
                  key={s.label}
                  to={to}
                  className="px-4 py-1 rounded-full border border-white/45 bg-white/10
                             text-sm font-medium text-white/90
                             hover:bg-[#03779B]/25 hover:border-[#2b9cb3] hover:text-white
                             transition"
                >
                  {s.label}
                </Link>
              );
            })}
          </div>
        </div>
      </main>

      {/* Popup (styled to match TarotApp.js) */}
      {/* In the EmotionPage component, update the popup section with these changes: */}
      {showPopup && (
        <BodyPortal>
          <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" aria-hidden="true" />
            <div
              role="dialog"
              aria-modal="true"
              className="relative z-10 w-[98%] sm:w-[95%] md:w-[500px] lg:w-[600px] max-h-[80vh] sm:max-h-[82vh] md:max-h-[85vh]
                   overflow-y-auto overscroll-contain backdrop-blur-xl
                   bg-gradient-to-b from-[#0B8FBF]/55 via-[#0B67AC]/30 to-[#0B67AE]/10
                   rounded-xl p-5 shadow-[0_10px_30px_rgba(0,0,0,0.25)] border border-white/60 text-[#072021] animate-fadeIn"
            >
              {/* Back button */}
              {popupStep !== 1 && !loading && (
                <button
                  onClick={handleBackButton}
                  className="absolute top-4 left-4 bg-white/20 text-[#f3eaff]/80
                       w-7 h-7 flex items-center justify-center
                       rounded-full text-xl font-normal
                       leading-none hover:text-[#0f3d3f]/70 hover:bg-white/60 transition"
                  aria-label="Back"
                >
                  ←
                </button>
              )}

              {/* Close button */}
              <button
                onClick={resetPopup}
                className="absolute top-4 right-4 bg-white/20 text-[#f3eaff]/80
                     w-7 h-7 flex items-center justify-center
                     rounded-full text-2xl font-normal
                     leading-none hover:text-[#0f3d3f]/70 hover:bg-white/60 transition"
                aria-label="Close"
              >
                ×
              </button>

              {/* Progress indicator */}
              <div className="flex justify-center mb-2 mt-2">
                <div className="flex space-x-2">
                  {[1, 2, 3].map((step) => (
                    <div key={step} className={`w-3 h-3 rounded-full ${popupStep >= step ? "bg-[#2b9cb3]" : "bg-white/30"}`} />
                  ))}
                </div>
              </div>

              {!readingStarted ? (
                <>
                  {/* Header per step */}
                  {popupStep === 1 ? (
                    <h3 className="text-xl font-bold text-[#f3eaff] mb-1 text-center">{meta.emotionKey}</h3>
                  ) : (
                    <h3 className="text-xl font-bold text-[#f3eaff] mb-1 text-center">
                      {spreads.find((s) => s.value === selectedSpread)?.title}
                    </h3>
                  )}

                  {/* separator */}
                  <div className="flex items-center justify-center mb-1">
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
                    <span className="px-4 text-[#f3eaff]/70 text-xl drop-shadow-[0_0_6px_rgba(203,183,234,0.8)]">✦</span>
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
                  </div>

                  {/* Step 1: Select Spread */}
                  {popupStep === 1 && (
                    <>
                      <p className="text-base font-normal mb-3 text-[#f3eaff]/80 text-center">
                        Select a 'card spread' below. Each tarot card will be drawn in tune with your emotion, offering insight that is uniquely yours.
                      </p>

                      <div className="flex flex-col gap-3 mb-2">
                        {spreads.map((s) => {
                          const selected = selectedSpread === s.value;
                          return (
                            <div
                              key={s.value}
                              onClick={() => {
                                setSelectedSpread(s.value);
                                if (!isOtherSelected) {
                                  setTimeout(() => setPopupStep(2), 300);
                                }
                              }}
                              className={`
                          rounded-md cursor-pointer p-3
                          transition-all duration-200
                          hover:-translate-y-0.5
                          hover:shadow-[0_12px_28px_rgba(88,64,120,0.4)]
                          focus:outline-none focus:ring-2 focus:ring-[#a991d4]/70
                          text-[#3f2d61]
                          backdrop-blur-md
                          bg-gradient-to-b from-[#0f3d3f]/5 via-[#14656a]/15 to-[#0f3d3f]/12
                          border-[1px] border-transparent
                          [background-clip:padding-box,border-box]
                          [border-image:linear-gradient(135deg,#a991d4,#cbb7ea,#cbb7ea,#2b9cb3,#2b9cb3)_1]
                          select-none
                          shadow-[0_4px_20px_0_rgba(0,0,0,0.8)]
                          ${selected ? "ring-2 ring-white scale-[1.02] bg-gradient-to-b from-[#2b9cb3]/20 via-[#2b9cb3]/30 to-[#2b9cb3]/20" : ""}`}
                            >
                              <div className="flex items-center justify-left gap-4">
                                <div className="text-base font-medium text-[#f3eaff]">{s.title}</div>
                                <img
                                  src={`/icons/${s.icon}`}
                                  alt={`${s.value} card spread icon`}
                                  className="h-6 object-contain drop-shadow-[0_1px_1px_rgba(169,145,212,0.3)]"
                                  draggable="false"
                                />
                              </div>

                              <div className="flex flex-col items-left text-left mt-2 select-none">
                                <p className="text-base italic text-white/80">{s.desc}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Cooldown message */}
                      {cooldownMessage && (
                        <div className="mt-4 p-3 bg-[#0f3d3f]/40 rounded-lg border border-[#2b9cb3]/50">
                          <p className="text-sm text-[#f3eaff]">
                            {cooldownMessage}
                            {cooldownSeconds > 0 && (
                              <span className="block mt-1 text-[#cbb7ea] font-medium">Please wait {cooldownSeconds} seconds</span>
                            )}
                          </p>
                        </div>
                      )}

                      {/* Google Ads placeholder */}
                     
                    </>
                  )}

                  {/* Step 2: Enter Whisper */}
                  {popupStep === 2 && (
                    <>
                      <p className="text-base font-normal mb-2 text-[#f3eaff] text-center">
                        {isOtherSelected
                          ? "To proceed with 'Other', please describe what you're feeling."
                          : "✦ For a deeper & more personalized tarot reading, also share your heart's whispers with the universe."}
                      </p>

                      <textarea
                        value={customWhisper}
                        onChange={(e) => setCustomWhisper(e.target.value)}
                        className="w-full border border-[#0f3d3f] bg-white rounded p-3 text-base text-gray-1000 placeholder-[#6f7373] mb-3"
                        rows={3}
                        placeholder={isOtherSelected ? "Write your emotion (required)" : "Write your whispers here (optional)"}
                      />

                      <p className="text-sm text-[#fff] italic mb-4 text-center">Center your spirit and let your truth rise</p>

                      {cooldownMessage && (
                        <div className="mb-4 p-3 bg-[#0f3d3f]/40 rounded-lg border border-[#2b9cb3]/50">
                          <p className="text-sm text-[#f3eaff]">
                            {cooldownMessage}
                            {cooldownSeconds > 0 && (
                              <span className="block mt-1 text-[#cbb7ea] font-medium">Please wait {cooldownSeconds} seconds</span>
                            )}
                          </p>
                        </div>
                      )}

                      <button
                        onClick={handleReadTarot}
                        className={`w-full px-6 py-3 rounded-xl text-lg font-bold transition-all duration-100 border-2
                    ${
                      canProceed && cooldownSeconds === 0
                        ? "bg-gradient-to-r from-[#2b9cb3] to-[#2b9cb3] border-white/50 text-white shadow-xl hover:shadow-lg hover:from-[#14656a] hover:via-[#2b9cb3] hover:to-[#14656a]"
                        : "bg-gray-200 text-gray-400 border-[#2b9cb3] cursor-not-allowed"
                    }`}
                        disabled={!canProceed || cooldownSeconds > 0}
                      >
                        {cooldownSeconds > 0 ? `Wait ${cooldownSeconds}s` : "Read My Tarot"}
                      </button>

                      {/* Google Ads placeholder */}
                      
                    </>
                  )}
                </>
              ) : loading ? (
                <div className="flex flex-col items-center justify-center py-12 animate-pulse">
                  <div className="text-lg font-bold text-[#f3eaff] text-center max-w-sm">Wait a moment please...</div>
                  <div className="text-lg text-[#f3eaff] italic text-center max-w-sm">
                    <br />
                    "In the hush before insight, the cards are being born from stardust and silence..."
                  </div>
                </div>
              ) : (
                <>
                  <h4 className="text-lg font-semibold text-[#f3eaff] mb-3 text-center">Your Mystic Tarot Card Reading</h4>

                  <div className="bg-white/90 bg-opacity-95 border border-[#cbb7ea] rounded-lg shadow-md p-6 text-left font-[Cormorant_Garamond] text-[#0A2829] text-[17px] leading-relaxed space-y-1">
                    {mockReading.split("\n\n").map((para, index, arr) => (
                      <div key={index}>
                        <p dangerouslySetInnerHTML={{ __html: formatTextWithMarkdown(para) }} className="whitespace-pre-wrap" />
                        {index < arr.length - 1 && <div className="flex justify-center my-1 text-[#021868] text-xl"></div>}
                      </div>
                    ))}
                  </div>

                  {/* Share + Copy */}
                  <div className="flex justify-between items-center mt-4 gap-2">
                    <button
                      onClick={async () => {
                        if (navigator.share) {
                          await navigator.share({ title: "My Tarot Reading ✨", text: mockReading });
                        } else {
                          alert("Sharing is not supported on your device.");
                        }
                      }}
                      className="text-sm text-white hover:text-white bg-[#14656a] hover:bg-[#2b9cb3]/70 border border-white hover:border-white px-4 py-1.5 rounded-full transition"
                    >
                      🔗 Share
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(mockReading);
                        setShowCopiedToast(true);
                        setTimeout(() => setShowCopiedToast(false), 2000);
                      }}
                      className="text-sm text-white hover:text-white bg-[#14656a] hover:bg-[#2b9cb3]/70 border border-white hover:border-white px-4 py-1.5 rounded-full transition"
                    >
                      🧾 Copy
                    </button>
                  </div>

                  <div className="flex flex-col items-end mt-1">
                    {showCopiedToast && <div className="mt-1 text-xs text-white/80 bg-transparent px-2 py-1">Copied ✓</div>}
                  </div>

                  {/* Donation */}
                  <div className="mt-6 bg-white/90 p-4 rounded-xl border border-[#decbaa]">
                    <p className="text-sm font-normal text-[#0f3d3f]">
                      If this space has brought you clarity, comfort, or even a moment of peace, consider leaving an offering to help keep the light on
                      for others who may wander in.
                    </p>
                    <div className="mt-4 flex flex-col items-center">
                      <button
                        onClick={() => setShowDonation(!showDonation)}
                        className="bg-[#14656a] text-white px-5 py-2 rounded-full shadow hover:bg-[#2b9cb3] hover:border-[#14656a] transition"
                      >
                        💖 Donate via PayPal
                      </button>
                      {showDonation && (
                        <div className="mt-4 w-full flex flex-col items-center space-y-3">
                          <div className="flex flex-wrap justify-center gap-3">
                            {donationAmounts.map((amount) => (
                              <a
                                key={amount}
                                href={`https://www.paypal.me/tarotfatecom/${amount}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-white border border-[#14656a] text-[#14656a] px-4 py-2 rounded-xl text-sm hover:bg-[#14656a] hover:text-white transition"
                              >
                                ${amount}
                              </a>
                            ))}
                          </div>
                          <div className="text-center mt-3">
                            <p className="text-sm text-[#14656a] mb-1">Or enter your own amount (max $1,000)</p>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={customAmount}
                              onChange={(e) => {
                                const value = e.target.value.trim();
                                const isValid = /^(\d+(\.\d{0,2})?)?$/.test(value);
                                if (isValid && parseFloat(value || "0") <= 1000) {
                                  setCustomAmount(value);
                                }
                              }}
                              className="border border-[#14656a] rounded px-3 py-1 w-32 text-center text-[#14656a]"
                              placeholder="Other ($)"
                            />
                            {customAmount && parseFloat(customAmount) > 0 && (
                              <div className="mt-2">
                                <a
                                  href={`https://www.paypal.me/tarotfatecom/${customAmount}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-block bg-[#14656a] text-white px-4 py-1.5 rounded-full shadow-lg hover:bg-[#2b9cb3] transition text-sm"
                                >
                                  Donate ${customAmount}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </BodyPortal>
      )}
    </div>
  );
}
